import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { calcularSlotsDisponibles, type Intervalo } from '../../lib/disponibilidad';
import { parseFechaLocal } from '../../lib/horarios';
import type { ClienteRow, ProfesionalRow, ServicioRow } from '../../types/database.types';

function formatoHora(d: Date): string {
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatearFechaHoy(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Crear cita manualmente (DAF v2, regla de negocio 14 — "el administrador
 * podrá crear citas manualmente"). A diferencia del portal público, esto
 * lo usa staff ya autenticado (admin/recepción), así que no pasa por el
 * RPC `crear_reserva_publica` (pensado para el rol `anon` sin sesión):
 * inserta directo en `citas`, protegido por la política RLS
 * `citas_staff_all` y por el `exclude using gist` de la tabla, que es
 * quien de verdad impide la doble reserva (regla de negocio 1) — el
 * cálculo de slots de aquí (igual que en el portal, vía
 * `calcularSlotsDisponibles`) es solo para no ofrecer horas obviamente
 * ocupadas, nunca la última palabra.
 *
 * A propósito no filtra por `disponible_online` ni `servicios.estado`
 * más allá de "activo": el admin puede agendar cualquier servicio activo
 * del salón manualmente, aunque no esté habilitado para el portal.
 *
 * `profesionalIdFijo` (nuevo, schema_profesional_ventas_agenda.sql): lo
 * pasa Agenda.tsx cuando quien agenda es una profesional -- la política
 * RLS `citas_profesional_insert` solo la deja crear citas con
 * `profesional_id = auth_profesional_id()` (a sí misma, nunca a otra),
 * así que aquí se refleja esa misma restricción en la interfaz: en vez
 * del selector de profesional, se muestra su propio nombre fijo, y la
 * lista de profesionales habilitadas para el servicio elegido se filtra
 * a solo ella (si no está habilitada para ese servicio, la lista queda
 * vacía y el formulario lo explica, igual que le pasaría a cualquiera).
 */
export function NuevaCitaForm({
  salonId,
  usuarioId,
  profesionalIdFijo,
  onCreada,
  onCancelar,
}: {
  salonId: string;
  usuarioId: string;
  profesionalIdFijo?: string | null;
  onCreada: () => void;
  onCancelar: () => void;
}) {
  const [clientes, setClientes] = useState<ClienteRow[]>([]);
  const [servicios, setServicios] = useState<ServicioRow[]>([]);
  const [profesionales, setProfesionales] = useState<ProfesionalRow[]>([]);

  const [clienteId, setClienteId] = useState('');
  const [servicioId, setServicioId] = useState('');
  const [profesionalId, setProfesionalId] = useState('');
  const [fecha, setFecha] = useState(formatearFechaHoy());
  const [observaciones, setObservaciones] = useState('');

  const [slots, setSlots] = useState<Intervalo[]>([]);
  const [slotElegido, setSlotElegido] = useState<Intervalo | null>(null);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const servicio = servicios.find((s) => s.id === servicioId) ?? null;

  useEffect(() => {
    supabase
      .from('clientes')
      .select('*')
      .eq('salon_id', salonId)
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => setClientes(data ?? []));
    supabase
      .from('servicios')
      .select('*')
      .eq('salon_id', salonId)
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => setServicios(data ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al elegir servicio, cargar las profesionales habilitadas para prestarlo.
  useEffect(() => {
    setProfesionalId('');
    setSlots([]);
    setSlotElegido(null);
    if (!servicioId) {
      setProfesionales([]);
      return;
    }
    supabase
      .from('profesional_servicios')
      .select('profesional_id')
      .eq('servicio_id', servicioId)
      .then(async ({ data: ps }) => {
        let ids = (ps ?? []).map((r) => r.profesional_id);
        if (profesionalIdFijo) {
          // Se está agendando como profesional: la lista se acota a ella
          // misma, sin importar quién más esté habilitada (ver RLS
          // citas_profesional_insert).
          ids = ids.filter((idProf) => idProf === profesionalIdFijo);
        }
        if (ids.length === 0) {
          setProfesionales([]);
          return;
        }
        const { data: profs } = await supabase
          .from('profesionales')
          .select('*')
          .eq('salon_id', salonId)
          .eq('estado', 'activo')
          .in('id', ids)
          .order('nombre');
        setProfesionales(profs ?? []);
        if (profesionalIdFijo && profs && profs.length === 1) {
          setProfesionalId(profs[0].id);
        }
      });
  }, [servicioId, salonId, profesionalIdFijo]);

  // Al elegir profesional/fecha, calcular slots disponibles ese día.
  useEffect(() => {
    setSlotElegido(null);
    if (!profesionalId || !fecha || !servicio) {
      setSlots([]);
      return;
    }
    cargarSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profesionalId, fecha, servicio]);

  async function cargarSlots() {
    if (!profesionalId || !servicio) return;
    setCargandoSlots(true);

    const fechaLocal = parseFechaLocal(fecha);
    const diaSemana = fechaLocal.getDay();

    const [{ data: horarios }, { data: bloqueosRaw }, { data: citasRaw }] = await Promise.all([
      supabase.from('horarios_profesionales').select('*').eq('profesional_id', profesionalId).eq('dia_semana', diaSemana),
      supabase
        .from('bloqueos_horarios')
        .select('*')
        .eq('salon_id', salonId)
        .or(`profesional_id.eq.${profesionalId},profesional_id.is.null`)
        .gte('fin', `${fecha}T00:00:00`)
        .lt('inicio', `${fecha}T23:59:59`),
      supabase
        .from('citas')
        .select('inicio, fin')
        .eq('profesional_id', profesionalId)
        .in('estado', ['pendiente', 'confirmada', 'atendida'])
        .gte('inicio', `${fecha}T00:00:00`)
        .lt('inicio', `${fecha}T23:59:59`),
    ]);

    if (!horarios || horarios.length === 0) {
      setSlots([]);
      setCargandoSlots(false);
      return;
    }

    const bloqueos: Intervalo[] = (bloqueosRaw ?? []).map((b) => ({ inicio: new Date(b.inicio), fin: new Date(b.fin) }));
    const citasExistentes: Intervalo[] = (citasRaw ?? []).map((c) => ({ inicio: new Date(c.inicio), fin: new Date(c.fin) }));

    const todos = horarios.flatMap((h) =>
      calcularSlotsDisponibles({
        fecha: fechaLocal,
        horaInicioLaboral: h.hora_inicio.slice(0, 5),
        horaFinLaboral: h.hora_fin.slice(0, 5),
        duracionServicioMinutos: servicio.duracion_minutos,
        citasExistentes,
        bloqueos,
      }),
    );
    todos.sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
    setSlots(todos);
    setCargandoSlots(false);
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!clienteId || !servicioId || !profesionalId || !slotElegido) return;
    setGuardando(true);
    setError(null);

    const { error: insertError } = await supabase.from('citas').insert({
      salon_id: salonId,
      cliente_id: clienteId,
      profesional_id: profesionalId,
      servicio_id: servicioId,
      inicio: slotElegido.inicio.toISOString(),
      fin: slotElegido.fin.toISOString(),
      estado: 'pendiente',
      origen: 'manual',
      observaciones: observaciones || null,
      usuario_creador_id: usuarioId,
    });

    setGuardando(false);

    if (insertError) {
      if (insertError.message.toLowerCase().includes('exclu') || insertError.code === '23P01') {
        setError('Esa hora se acaba de ocupar para esa profesional. Elige otro horario.');
        cargarSlots();
      } else {
        setError(`No se pudo crear la cita: ${insertError.message}`);
      }
      return;
    }

    onCreada();
  }

  return (
    <form onSubmit={enviar} className="card mb-6 space-y-4">
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Campo label="Clienta">
          <div className="flex items-center gap-2">
            <select required className="input-field" value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">Selecciona…</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre} {c.apellido ?? ''}
                </option>
              ))}
            </select>
            <Link to="/admin/clientas/nueva" className="whitespace-nowrap text-xs text-gold-deep hover:underline">
              + Nueva
            </Link>
          </div>
        </Campo>

        <Campo label="Servicio">
          <select required className="input-field" value={servicioId} onChange={(e) => setServicioId(e.target.value)}>
            <option value="">Selecciona…</option>
            {servicios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre} ({s.duracion_minutos} min)
              </option>
            ))}
          </select>
        </Campo>

        <Campo label="Profesional">
          {profesionalIdFijo ? (
            <>
              <input
                className="input-field bg-onyx/5"
                value={
                  servicioId
                    ? (profesionales[0]?.nombre ?? (profesionales.length === 0 ? 'No habilitada para este servicio' : ''))
                    : 'Elige un servicio primero'
                }
                disabled
                readOnly
              />
              {servicioId && profesionales.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  No estás habilitada para prestar este servicio -- elige otro.
                </p>
              )}
            </>
          ) : (
            <>
              <select
                required
                className="input-field"
                value={profesionalId}
                onChange={(e) => setProfesionalId(e.target.value)}
                disabled={!servicioId}
              >
                <option value="">{servicioId ? 'Selecciona…' : 'Elige un servicio primero'}</option>
                {profesionales.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombre}
                  </option>
                ))}
              </select>
              {servicioId && profesionales.length === 0 && (
                <p className="mt-1 text-xs text-amber-700">Ninguna profesional activa está habilitada para este servicio.</p>
              )}
            </>
          )}
        </Campo>

        <Campo label="Fecha">
          <input required type="date" className="input-field" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </Campo>
      </div>

      {profesionalId && servicioId && (
        <div>
          <span className="mb-2 block text-sm font-medium text-onyx/80">Hora disponible</span>
          {cargandoSlots && <p className="text-sm text-onyx/50">Buscando horas disponibles…</p>}
          {!cargandoSlots && slots.length === 0 && (
            <p className="text-sm text-onyx/50">Sin horas disponibles ese día para esta profesional.</p>
          )}
          {!cargandoSlots && slots.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {slots.map((s) => (
                <button
                  key={s.inicio.toISOString()}
                  type="button"
                  onClick={() => setSlotElegido(s)}
                  className={`rounded-md border px-3 py-1.5 text-sm ${
                    slotElegido?.inicio.getTime() === s.inicio.getTime()
                      ? 'border-gold-deep bg-gold/15 text-gold-deep'
                      : 'border-onyx/15 text-onyx/70 hover:border-onyx/30'
                  }`}
                >
                  {formatoHora(s.inicio)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Campo label="Observaciones (opcional)">
        <textarea
          className="input-field"
          rows={2}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </Campo>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancelar} className="btn-secondary">
          Cancelar
        </button>
        <button type="submit" disabled={guardando || !slotElegido} className="btn-primary disabled:opacity-50">
          {guardando ? 'Creando…' : 'Crear cita'}
        </button>
      </div>
    </form>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">{label}</span>
      {children}
    </label>
  );
}
